<script setup lang="ts">
import * as yup from 'yup'
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { reactive, onMounted } from 'vue'
import Backend from '@/models/backend.ts'
import FormProcess from '@/models/formProcess.ts'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'

const backend = new Backend()
const formProcess = new FormProcess()
const gameStore = useGameStore()
const allianceStore = useAllianceStore()

// Define the validation schema
const schema = yup.object().shape({
  allianceDescription: yup.string().max(1024, () => `Do not use more than 1024 characters`)
})

// Form values
const formValues = reactive<{ [key: string]: string }>({
  allianceDescription: ''
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  allianceDescription: ''
})

// Load current alliance data
onMounted(() => {
  const alliance = allianceStore.getAlliance(gameStore.player!.allianceId)
  if (alliance) {
    formValues.allianceDescription = alliance.description
  }
})

// Handle form submission
const submit = () => {
  schema
    .validate(formValues)
    .then(() => {
      formProcess.resetFormErrors(formErrors)

      backend
        .updateAllianceDescription(formValues.allianceDescription)
        .then(() => {
          gameStore.setTinySuccessToastMessage('Alliance description updated')
          gameStore.modalType = ''
        })
        .catch((error) => formProcess.processBackendErrorResponse(error, formErrors))
    })
    .catch((error) => formProcess.processFrontendErrorResponse(error, formErrors))
}
</script>

<template>
  <SmallModel title="Edit Alliance Description">
    <form method="post" @submit.prevent="submit" class="p-3">
      <div class="mb-3">
        <label for="allianceDescription" class="block mb-1 text-sm font-medium text-slate-300"
          >Alliance description</label
        >
        <textarea
          id="allianceDescription"
          v-model="formValues.allianceDescription"
          rows="4"
          class="border text-sm rounded-lg block w-full p-2.5 bg-slate-800 border-slate-700 text-white focus:ring-blue-500 focus:border-blue-500"
          :class="{ 'border-red-500': formErrors.allianceDescription }"
          autofocus
        ></textarea>
        <p v-if="formErrors.allianceDescription" class="mt-1 text-xs text-red-500">
          {{ formErrors.allianceDescription }}
        </p>
      </div>

      <div class="flex justify-between pt-2">
        <FormGenericError :error="formErrors.generic" />
        <ActionButton type="submit">Update Description</ActionButton>
      </div>
    </form>
  </SmallModel>
</template>

<style scoped lang="scss"></style> 